import React, { useEffect } from "react"
import styles from "./TicketSummary.module.scss";
import { RootState } from "store/store";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useSelector, useDispatch} from "react-redux";
import { useParams } from "react-router-dom";
import { ReactComponent as IconPencilAI } from "assets/icons/pencil-ai.svg";
import { TicketSummarySentiment } from "components/ticket-summary-sentiment/TicketSummarySentiment";
import { fetchSummary, setError } from "store/ticketSummarySlice";
import Skeleton from '@mui/material/Skeleton';
import useResizeObserver from "hooks/useResizeObserver";
import { Configuration } from "core/configuration";
import { EventLogger } from "core/eventLogger";
import { DEFAULT_MESSAGE, LogEventEnum } from "appConstants";

export const TicketSummary: React.FC = () => {
    useResizeObserver(Configuration.innerRootId);

    const {unit_id} = useParams<{unit_id: string}>();
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
    const isLoading = useSelector((state: RootState) => state.ticketOwnerSummary.isLoadingFetch);
    const summary = useSelector((state: RootState) => state.ticketOwnerSummary.summary);
    const error = useSelector((state: RootState) => state.ticketOwnerSummary.error);
    const existTopics = Boolean(summary?.topics.length);

    useEffect(() => {
        if(unit_id && Number(unit_id) > 0) {
            dispatch(fetchSummary(Number(unit_id)));
        } else {
            dispatch(setError(DEFAULT_MESSAGE.UNIT_ID));
        }
    },[unit_id, dispatch]);

    useEffect(() => {
        if(existTopics) EventLogger.emitSuccessEvent({logEvent: LogEventEnum.CATEGORICAL_SENTIMENTS_LOADED});
    },[existTopics, dispatch]);

    // Error Managemenet
    if(error) return <div className={styles.container} data-testid="ticket-owner-summary-error">
        {error}
    </div>
    

    // Early return when loading ....
    if(isLoading) return <div className={styles.container} data-testid="ticket-owner-summary-loader">
        <Skeleton width={100}/> 
        <div className={styles.highlevel}>    
            <Skeleton width={100}/> 
            <Skeleton/> 
            <Skeleton/> 
            <Skeleton/> 
        </div><br/><br/>
        <Skeleton width={100}/><br/>
        <div className={styles.categorical}>
            <div className={styles.header}>
                <Skeleton/>
            </div>
            <div className={styles.topics}>
                <br/>
                <Skeleton height={30}/>
                <Skeleton height={30}/>
                <Skeleton height={30}/>
                <br/>
            </div>
        </div>
    </div>
    
    // State Ready Component
    return (
        <div className={styles.container} data-testid="ticket-owner-summary">
            {/* Categorical Sentinments */}
            { existTopics 
                ?
                <>
                    {/* High Level */}
                    <h2>High Level</h2>
                    <hr/>
                    <div className={styles.highlevel}>
                        <h3>AI Summary Of All Tickets</h3>
                        
                        <div className={styles.summary} data-testid="ticket-owner-summary-overall">
                            {summary?.overall_summary}
                        </div>
                        
                        <div className={styles.about}>
                            <IconPencilAI />
                            <label>
                                AI-generated using ticket descriptions, details, and owner-facing comments for this property.
                            </label>
                        </div>
                    </div>
                    <h2>Categorical Sentiments</h2>
                    <hr/>
                    <div className={styles.categorical}>
                        <div className={styles.header}>
                            <div>topic</div>
                            <div>tickets/cases</div>
                            <div>summary</div>
                        </div>
                        <div className={styles.topics} data-testid="ticket-owner-summary-sentiments">
                            { summary?.topics.map( (topic, idx) => (
                                <TicketSummarySentiment
                                    key={idx}
                                    topic={topic.topic}
                                    ticketIds={topic.ticket_ids}
                                    caseNumbers={topic.case_numbers}
                                    summary={topic.topic_summary}
                                />
                            ))} 
                        </div>
                    </div>
                </>
                :
                <div className={styles.empty} data-testid="ticket-owner-summary-no-sentiments">
                    {DEFAULT_MESSAGE.CATEGORICAL_SENTIMENTS}
                </div>
            }
        </div>
    )
}
